/**
 * API-tokens — je eigen tokens aanmaken, bekijken en intrekken.
 *
 * Een token handelt namens jou: dezelfde rollen en permissies, met een extra
 * plafond (`scope`). Het geheim komt één keer terug, direct na het aanmaken;
 * daarna toont de lijst alleen het voorvoegsel.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, type ApiTokenDTO, type ApiTokenScope, api } from "../api/client";
import { confirmDialog } from "../editor/confirmDialog";
import { useSession } from "../state/session";
import { ld, pill } from "./ledgerUi";

const SCOPES: ApiTokenScope[] = ["read", "model", "admin"];
const EXPIRY_OPTIONS = [30, 90, 365, 0];

function explain(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

export function ApiTokensSettings() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const [tokens, setTokens] = useState<ApiTokenDTO[] | null>(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ApiTokenScope>("model");
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [expiry, setExpiry] = useState(90);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.listApiTokens();
      setTokens(res.tokens);
    } catch (err) {
      setError(explain(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.createApiToken({
        name: name.trim(),
        scope,
        workspace_id: workspaceId || null,
        expires_in_days: expiry,
        client: "manual",
      });
      setCreated(res.token);
      setCopied(false);
      setName("");
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (tok: ApiTokenDTO) => {
    if (
      !(await confirmDialog({
        message: t("apiTokens.revokeConfirm", { name: tok.name }),
        danger: true,
      }))
    )
      return;
    setError(null);
    try {
      await api.revokeApiToken(tok.id);
      await refresh();
    } catch (err) {
      setError(explain(err));
    }
  };

  const onAiPlateauOnly = async (tok: ApiTokenDTO, value: boolean) => {
    setError(null);
    try {
      await api.setApiTokenAiPlateauOnly(tok.id, value);
      await refresh();
    } catch (err) {
      setError(explain(err));
    }
  };

  const onCopy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created);
    setCopied(true);
  };

  const workspaceName = (id: string | null) =>
    id ? (me?.workspaces.find((w) => w.workspace_id === id)?.workspace_name ?? id) : "—";

  const active = (tokens ?? []).filter((tok) => !tok.revoked_at);
  const revoked = (tokens ?? []).filter((tok) => tok.revoked_at);

  const row = (tok: ApiTokenDTO) => (
    <li
      key={tok.id}
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`font-medium text-sm ${tok.revoked_at ? "text-(--ld-muted)" : ""}`}>
            {tok.name}
          </span>
          <span className={pill.neutral}>{t(`apiTokens.scopes.${tok.scope}`)}</span>
          {tok.revoked_at && <span className={pill.negative}>{t("apiTokens.revoked")}</span>}
        </div>
        <p className="mt-1 text-(--ld-muted) text-xs leading-5">
          <span className="font-mono">{tok.token_prefix}…</span>
          {" · "}
          {t("apiTokens.workspace")}: {workspaceName(tok.workspace_id)}
          {" · "}
          {t("apiTokens.colClient")}: {tok.client}
          {" · "}
          <span className="whitespace-nowrap">
            {t("apiTokens.colLastUsed")}: {tok.last_used_at ?? t("apiTokens.never")}
          </span>
          {" · "}
          <span className="whitespace-nowrap">
            {t("apiTokens.colExpires")}: {tok.expires_at ?? t("apiTokens.never")}
          </span>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        {tok.client === "mcp" &&
          (tok.revoked_at ? (
            <span className="text-(--ld-muted) text-xs">
              {tok.ai_plateau_only ? t("apiTokens.aiPlateauOnly") : t("apiTokens.aiAnywhere")}
            </span>
          ) : (
            <label className="flex items-center gap-2 text-(--ld-muted) text-xs">
              {t("apiTokens.colAi")}
              <select
                className={`${ld.inputInline} h-8 text-[13px]`}
                value={tok.ai_plateau_only ? "plateau" : "anywhere"}
                title={t("apiTokens.aiToggleTitle")}
                onChange={(e) => void onAiPlateauOnly(tok, e.target.value === "plateau")}
              >
                <option value="plateau">{t("apiTokens.aiPlateauOnly")}</option>
                <option value="anywhere">{t("apiTokens.aiAnywhere")}</option>
              </select>
            </label>
          ))}
        {!tok.revoked_at && (
          <button type="button" onClick={() => onRevoke(tok)} className={ld.textDanger}>
            {t("apiTokens.revoke")}
          </button>
        )}
      </div>
    </li>
  );

  return (
    <div className="flex flex-col gap-8">
      <p className={ld.viewIntro}>{t("apiTokens.intro")}</p>

      {error && (
        <p role="alert" className={ld.error}>
          {error}
        </p>
      )}

      {created ? (
        <section className="rounded-lg bg-(--ld-notice-bg) p-4 text-(--ld-text)">
          <h3 className={ld.sectionTitle}>{t("apiTokens.createdTitle")}</h3>
          <p className="mt-1 text-sm leading-6">{t("apiTokens.createdBody")}</p>
          <div className="mt-3 flex gap-2">
            <code className="flex-1 select-all break-all rounded-md border border-(--ld-control) bg-(--ld-surface) px-3 py-2 font-mono text-xs">
              {created}
            </code>
            <button type="button" onClick={onCopy} className={ld.secondary}>
              {copied ? t("apiTokens.copied") : t("apiTokens.copy")}
            </button>
          </div>
          <button type="button" onClick={() => setCreated(null)} className={`mt-3 ${ld.primary}`}>
            {t("apiTokens.done")}
          </button>
        </section>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy && name.trim()) void onCreate();
          }}
        >
          <h3 className={ld.sectionTitle}>{t("apiTokens.newTitle")}</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="token-name" className={ld.label}>
                {t("apiTokens.name")}
              </label>
              <input
                id="token-name"
                className={ld.input}
                value={name}
                maxLength={80}
                placeholder={t("apiTokens.namePlaceholder")}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="token-workspace" className={ld.label}>
                {t("apiTokens.workspace")}
              </label>
              <select
                id="token-workspace"
                className={ld.input}
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                disabled={busy}
              >
                <option value="">{t("apiTokens.allWorkspaces")}</option>
                {me?.workspaces.map((w) => (
                  <option key={w.workspace_id} value={w.workspace_id}>
                    {w.workspace_name}
                  </option>
                ))}
              </select>
            </div>
            <fieldset>
              <legend className={ld.label}>{t("apiTokens.scopeLabel")}</legend>
              <div className="mt-2 flex flex-col gap-2">
                {SCOPES.map((s) => (
                  <label key={s} className="flex items-start gap-2.5 text-sm">
                    <input
                      type="radio"
                      name="api-token-scope"
                      className="mt-1 accent-(--ld-primary)"
                      checked={scope === s}
                      onChange={() => setScope(s)}
                      disabled={busy}
                    />
                    <span>
                      <span className="font-medium">{t(`apiTokens.scopes.${s}`)}</span>
                      <span className="block text-(--ld-muted) text-[13px] leading-5">
                        {t(`apiTokens.scopeHelp.${s}`)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div>
              <label htmlFor="token-expiry" className={ld.label}>
                {t("apiTokens.expiry")}
              </label>
              <select
                id="token-expiry"
                className={ld.input}
                value={expiry}
                onChange={(e) => setExpiry(Number(e.target.value))}
                disabled={busy}
              >
                {EXPIRY_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d === 0 ? t("apiTokens.expiryNever") : t("apiTokens.expiryDays", { count: d })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <button type="submit" disabled={busy || !name.trim()} className={ld.primary}>
              {busy ? t("apiTokens.creating") : t("apiTokens.create")}
            </button>
          </div>
        </form>
      )}

      <section className="border-(--ld-rule) border-t pt-8">
        <h3 className={`${ld.sectionTitle} mb-3`}>{t("apiTokens.listTitle")}</h3>
        {tokens === null ? null : active.length === 0 ? (
          <p className={`${ld.well} text-(--ld-muted) text-sm`}>{t("apiTokens.empty")}</p>
        ) : (
          <ul className={ld.list}>{active.map(row)}</ul>
        )}
        {revoked.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              className={ld.textButton}
              aria-expanded={showRevoked}
              onClick={() => setShowRevoked((v) => !v)}
            >
              {showRevoked
                ? t("apiTokens.hideRevoked")
                : t("apiTokens.showRevoked", { count: revoked.length })}
            </button>
            {showRevoked && <ul className={`mt-3 ${ld.list}`}>{revoked.map(row)}</ul>}
          </div>
        )}
      </section>

      <section className={ld.well}>
        <h3 className="font-medium text-sm">{t("apiTokens.mcpTitle")}</h3>
        <p className="mt-1 text-pretty text-(--ld-muted) text-sm leading-6">
          {t("apiTokens.mcpBody")}
        </p>
      </section>
    </div>
  );
}
